import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { DataPoint } from '../../utils/MarketDataGenerator';
import { X, RotateCcw } from 'lucide-react';
import { useFlowaesStore } from '../../store/useFlowaesStore';
import { CoreHUD } from '../layout/CoreHUD';
import { useWaveGeometry, useWaveColors } from '../../utils/waveGeometry';
import { getTimeframeSeparation, getPeriodLabel, getSeparatorIndices, getDynamicPeriodSeparation } from '../../utils/timeUtils';

const hexToRgba = (hex: string, opacity: number) => {
  if (!hex) return 'rgba(255,255,255,0.1)';
  const c = hex.replace('#', '');
  if (c.length !== 6) return hex;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const formatAbbrev = (val: number, decimals: number) => {
  if (Math.abs(val) >= 1000) {
    return (val / 1000).toFixed(1) + 'K';
  }
  return val.toFixed(decimals);
};

const getTickInterval = (min: number, max: number, maxTicks: number) => {
  const range = Math.abs(max - min);
  if (range <= 0 || !isFinite(range)) return 1;
  const roughStep = range / maxTicks;
  const stepPower = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalizedStep = roughStep / stepPower;
  let niceStep = 1;
  if (normalizedStep > 5) niceStep = 10;
  else if (normalizedStep > 2) niceStep = 5;
  else if (normalizedStep > 1) niceStep = 2;
  return niceStep * stepPower;
};

const getTimeTickInterval = (min: number, max: number, maxTicks: number) => {
  const range = Math.abs(max - min);
  if (range <= 0 || !isFinite(range)) return 60000;
  const rough = range / maxTicks;
  const steps = [
    1000, 2000, 5000, 10000, 15000, 30000,
    60000, 120000, 300000, 600000, 900000, 1800000,
    3600000, 7200000, 14400000, 28800000,
    86400000, 86400000 * 2, 86400000 * 7
  ];
  for (const step of steps) {
    if (step >= rough) return step;
  }
  return steps[steps.length - 1];
};

const formatTime = (ts: number, rangeMs: number) => {
  const d = new Date(ts);
  if (rangeMs > 24 * 60 * 60 * 1000 * 3) {
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

interface BackWall2DChartProps {
  isOpen: boolean;
  onClose: () => void;
  data: DataPoint[];
  symbol?: string;
  timeframe?: string;
  isEditingSymbol?: boolean;
  setIsEditingSymbol?: (val: boolean) => void;
  setSymbol?: (val: string) => void;
  lastPrice?: number | null;
  priceChange?: number;
  latencyMs?: number | null;
}

export const BackWall2DChart: React.FC<BackWall2DChartProps> = ({
  isOpen,
  onClose,
  data,
  symbol = 'BTCUSDT',
  timeframe = '1m',
  isEditingSymbol = false,
  setIsEditingSymbol = () => {},
  setSymbol = () => {},
  lastPrice = null,
  priceChange = 0,
  latencyMs = null
}) => {
  const [labelOpacity, setLabelOpacity] = useState(0);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const renderCanvasRef = useRef<(() => void) | null>(null);

  const scheduleRender = useCallback(() => {
    if (renderCanvasRef.current) requestAnimationFrame(renderCanvasRef.current);
  }, []);

  const resetTransform = useCallback(() => {
    transformRef.current = { panX: 0, panY: 0, zoomX: 1, zoomY: 1 };
    scheduleRender();
  }, [scheduleRender]);

  useEffect(() => {
    if (isOpen) {
      setLabelOpacity(1);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = setTimeout(() => {
        setLabelOpacity(0);
      }, 20000);
    } else {
      setLabelOpacity(0);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    }
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [isOpen]);

  const colorUp = useFlowaesStore(s => s.colorUp);
  const colorDown = useFlowaesStore(s => s.colorDown);
  const buyersColor = useFlowaesStore(s => s.buyersColor);
  const sellersColor = useFlowaesStore(s => s.sellersColor);
  const gridColor = useFlowaesStore(s => s.gridColor);
  const gridOpacity = useFlowaesStore(s => s.gridOpacity);
  const gridThickness = useFlowaesStore(s => s.gridThickness);
  const showPeriodSeparators = useFlowaesStore(s => s.showPeriodSeparators);
  const periodColor = useFlowaesStore(s => s.periodColor);
  const periodOpacity = useFlowaesStore(s => s.periodOpacity);
  const periodLineStyle = useFlowaesStore(s => s.periodLineStyle);
  const periodLineWidth = useFlowaesStore(s => s.periodLineWidth);

  const colorIntensity = useFlowaesStore(s => s.colorIntensity);
  const colorContrast = useFlowaesStore(s => s.colorContrast);

  const width3D = useFlowaesStore(s => s.width3D);
  const heightScale3D = useFlowaesStore(s => s.heightScale3D);
  const zLimit3D = useFlowaesStore(s => s.zLimit3D);
  const momentumSensitivity = useFlowaesStore(s => s.momentumSensitivity);
  const curveTension = useFlowaesStore(s => s.curveTension);
  const historyShadowStrength = useFlowaesStore(s => s.historyShadowStrength);
  const sideHistoryShadowStrength = useFlowaesStore(s => s.sideHistoryShadowStrength);
  const sma10Period = useFlowaesStore(s => s.sma10Period);
  const sma20Period = useFlowaesStore(s => s.sma20Period);
  const sma50Period = useFlowaesStore(s => s.sma50Period);
  const showSMA10 = useFlowaesStore(s => s.showSMA10);
  const showSMA20 = useFlowaesStore(s => s.showSMA20);
  const showSMA50 = useFlowaesStore(s => s.showSMA50);
  
  const layout = useMemo(() => ({ width3D, heightScale3D, zLimit3D, startX: -width3D / 2 }), [width3D, heightScale3D, zLimit3D]);
  const config = useMemo(() => ({
    momentumSensitivity, curveTension, historyShadowStrength, sideHistoryShadowStrength,
    sma10Period, sma20Period, sma50Period, showSMA10, showSMA20, showSMA50, fullColors3D: new Float32Array()
  }), [momentumSensitivity, curveTension, historyShadowStrength, sideHistoryShadowStrength, sma10Period, sma20Period, sma50Period, showSMA10, showSMA20, showSMA50]);
  
  const { getY, minP, maxP } = useWaveGeometry(data, layout, config);
  
  const rgbaGrid = hexToRgba(gridColor, gridOpacity);
  
  // NATIVE CANVAS REFS
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const transformRef = useRef({ panX: 0, panY: 0, zoomX: 1, zoomY: 1 });
  
  const activePointers = useRef<Map<number, {x: number, y: number}>>(new Map());
  const initialPinchDistance = useRef<number | null>(null);
  const initialPinchZoom = useRef<{x: number, y: number}>({x: 1, y: 1});
  const dragModeRef = useRef<'pan' | 'axisX' | 'axisY'>('pan');
  const dragContextRef = useRef<{
    anchorScreenX?: number;
    anchorDataX?: number;
    anchorScreenY?: number;
    anchorDataY?: number;
    initialZoomX?: number;
    initialZoomY?: number;
    initialPointerX?: number;
    initialPointerY?: number;
  }>({});

  const handlePointerDown = (e: React.PointerEvent) => {
    if (activePointers.current.size === 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const axisXThreshold = rect.height - 30;
      const axisYThreshold = rect.width - 60;
      
      if (y > axisXThreshold && x < axisYThreshold) {
        dragModeRef.current = 'axisX';
      } else if (x > axisYThreshold && y < axisXThreshold) {
        dragModeRef.current = 'axisY';
      } else {
        dragModeRef.current = 'pan';
      }

      // Anchored Zoom Context Setup
      const boxW = Math.max(100, rect.width);
      const boxH = Math.max(100, rect.height);
      const scaleX = boxW / width3D;
      const scaleY = boxH / heightScale3D;
      const baseScaleX = scaleX * transformRef.current.zoomX;
      const baseScaleY = scaleY * transformRef.current.zoomY;
      const centerX = boxW / 2 + transformRef.current.panX;
      const centerY = boxH / 2 + transformRef.current.panY;
      
      if (dragModeRef.current === 'axisX') {
        const validCandles = data.filter(d => (d as any).isRealCandle !== false && d.open !== undefined);
        let targetCandle = validCandles[validCandles.length - 1];
        
        for (let i = validCandles.length - 1; i >= 0; i--) {
          const c = validCandles[i];
          if (c && typeof c.time === 'number') {
            const cDataX = layout.startX + c.time * width3D;
            const cScreenX = centerX + cDataX * baseScaleX;
            if (cScreenX <= boxW) {
              targetCandle = c;
              break;
            }
          }
        }
        
        if (targetCandle && typeof targetCandle.time === 'number') {
          const anchorDataX = layout.startX + targetCandle.time * width3D;
          const anchorScreenX = centerX + anchorDataX * baseScaleX;
          dragContextRef.current = {
            anchorScreenX,
            anchorDataX,
            initialZoomX: transformRef.current.zoomX,
            initialPointerX: e.clientX
          };
        }
      } else if (dragModeRef.current === 'axisY') {
        const anchorScreenY = y;
        const anchorDataY = (centerY - anchorScreenY) / baseScaleY;
        dragContextRef.current = {
          anchorScreenY,
          anchorDataY,
          initialZoomY: transformRef.current.zoomY,
          initialPointerY: e.clientY
        };
      }
    }
    
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture(e.pointerId);
    
    if (activePointers.current.size === 2) {
      const pts = Array.from(activePointers.current.values());
      initialPinchDistance.current = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      initialPinchZoom.current = { x: transformRef.current.zoomX, y: transformRef.current.zoomY };

      const rect = e.currentTarget.getBoundingClientRect();
      const boxW = Math.max(100, rect.width);
      const boxH = Math.max(100, rect.height);
      const scaleX = boxW / width3D;
      const scaleY = boxH / heightScale3D;
      const baseScaleX = scaleX * transformRef.current.zoomX;
      const baseScaleY = scaleY * transformRef.current.zoomY;
      const centerX = boxW / 2 + transformRef.current.panX;
      const centerY = boxH / 2 + transformRef.current.panY;

      const midClientX = (pts[0].x + pts[1].x) / 2;
      const midClientY = (pts[0].y + pts[1].y) / 2;
      const midScreenX = midClientX - rect.left;
      const midScreenY = midClientY - rect.top;
      
      const anchorDataX = (midScreenX - centerX) / baseScaleX;
      const anchorDataY = (centerY - midScreenY) / baseScaleY;
      
      dragContextRef.current = {
        anchorDataX,
        anchorDataY,
        initialZoomX: transformRef.current.zoomX,
        initialZoomY: transformRef.current.zoomY,
      };
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!activePointers.current.has(e.pointerId)) return;
    
    if (activePointers.current.size === 1) {
      const lastPos = activePointers.current.get(e.pointerId)!;
      const dx = e.clientX - lastPos.x;
      const dy = e.clientY - lastPos.y;
      
      if (dragModeRef.current === 'axisX' && dragContextRef.current.anchorScreenX !== undefined) {
        const totalDx = e.clientX - dragContextRef.current.initialPointerX!;
        const factor = Math.exp(totalDx * 0.005);
        const newZoomX = Math.max(0.1, Math.min(dragContextRef.current.initialZoomX! * factor, 50));
        transformRef.current.zoomX = newZoomX;
        
        const boxW = Math.max(100, e.currentTarget.getBoundingClientRect().width);
        const scaleX = boxW / width3D;
        const newBaseScaleX = scaleX * newZoomX;
        
        transformRef.current.panX = dragContextRef.current.anchorScreenX - (boxW / 2) - (dragContextRef.current.anchorDataX! * newBaseScaleX);
      } else if (dragModeRef.current === 'axisY' && dragContextRef.current.anchorScreenY !== undefined) {
        const totalDy = e.clientY - dragContextRef.current.initialPointerY!;
        const factor = Math.exp(-totalDy * 0.005);
        const newZoomY = Math.max(0.1, Math.min(dragContextRef.current.initialZoomY! * factor, 50));
        transformRef.current.zoomY = newZoomY;
        
        const boxH = Math.max(100, e.currentTarget.getBoundingClientRect().height);
        const scaleY = boxH / heightScale3D;
        const newBaseScaleY = scaleY * newZoomY;
        
        transformRef.current.panY = dragContextRef.current.anchorScreenY - (boxH / 2) + (dragContextRef.current.anchorDataY! * newBaseScaleY);
      } else if (dragModeRef.current === 'pan') {
        transformRef.current.panX += dx;
        transformRef.current.panY += dy;
      }
      scheduleRender();
      activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    } else if (activePointers.current.size === 2) {
      activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const pts = Array.from(activePointers.current.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      
      if (initialPinchDistance.current && dragContextRef.current.anchorDataX !== undefined && dragContextRef.current.anchorDataY !== undefined) {
        const zoomFactor = dist / initialPinchDistance.current;
        const newZoomX = Math.max(0.1, Math.min(dragContextRef.current.initialZoomX! * zoomFactor, 50));
        const newZoomY = Math.max(0.1, Math.min(dragContextRef.current.initialZoomY! * zoomFactor, 50));
        transformRef.current.zoomX = newZoomX;
        transformRef.current.zoomY = newZoomY;

        const midClientX = (pts[0].x + pts[1].x) / 2;
        const midClientY = (pts[0].y + pts[1].y) / 2;
        const rect = e.currentTarget.getBoundingClientRect();
        const midScreenX = midClientX - rect.left;
        const midScreenY = midClientY - rect.top;
        
        const boxW = Math.max(100, rect.width);
        const boxH = Math.max(100, rect.height);
        const scaleX = boxW / width3D;
        const scaleY = boxH / heightScale3D;
        const newBaseScaleX = scaleX * newZoomX;
        const newBaseScaleY = scaleY * newZoomY;

        transformRef.current.panX = midScreenX - (boxW / 2) - (dragContextRef.current.anchorDataX * newBaseScaleX);
        transformRef.current.panY = midScreenY - (boxH / 2) + (dragContextRef.current.anchorDataY * newBaseScaleY);
        scheduleRender();
      }
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    activePointers.current.delete(e.pointerId);
    if (activePointers.current.size < 2) {
      initialPinchDistance.current = null;
    }
    if (activePointers.current.size === 0) {
      dragContextRef.current = {};
    }
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      
      const rect = e.currentTarget.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      
      const boxW = Math.max(100, rect.width);
      const boxH = Math.max(100, rect.height);
      const scaleX = boxW / width3D;
      const scaleY = boxH / heightScale3D;
      const baseScaleX = scaleX * transformRef.current.zoomX;
      const baseScaleY = scaleY * transformRef.current.zoomY;
      const centerX = boxW / 2 + transformRef.current.panX;
      const centerY = boxH / 2 + transformRef.current.panY;
      
      const anchorDataX = (screenX - centerX) / baseScaleX;
      const anchorDataY = (centerY - screenY) / baseScaleY;

      const zoomFactor = -e.deltaY * 0.005;
      const zoomSpeed = 1.05;
      const newZoomX = Math.max(0.1, Math.min(transformRef.current.zoomX * (zoomFactor > 0 ? zoomSpeed : 1 / zoomSpeed), 50));
      const newZoomY = Math.max(0.1, Math.min(transformRef.current.zoomY * (zoomFactor > 0 ? zoomSpeed : 1 / zoomSpeed), 50));
      
      transformRef.current.zoomX = newZoomX;
      transformRef.current.zoomY = newZoomY;

      const newBaseScaleX = scaleX * newZoomX;
      const newBaseScaleY = scaleY * newZoomY;

      transformRef.current.panX = screenX - (boxW / 2) - (anchorDataX * newBaseScaleX);
      transformRef.current.panY = screenY - (boxH / 2) + (anchorDataY * newBaseScaleY);
    } else {
      transformRef.current.panX -= e.deltaX;
      transformRef.current.panY -= e.deltaY;
    }
    scheduleRender();
  };
  
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    if (data.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = container.clientWidth;
    const height = container.clientHeight;
    
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }
    
    ctx.resetTransform();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const margin = { left: 0, right: 0, top: 0, bottom: 0 };
    const boxLeft = margin.left;
    const boxTop = margin.top;
    const boxW = Math.max(100, width - margin.left - margin.right);
    const boxH = Math.max(100, height - margin.top - margin.bottom);
    const boxRight = boxLeft + boxW;
    const boxBottom = boxTop + boxH;

    const { panX, panY, zoomX, zoomY } = transformRef.current;
    const startX = layout.startX;
    
    const scaleX = boxW / width3D;
    const scaleY = boxH / heightScale3D;
    const baseScaleX = scaleX * zoomX;
    const baseScaleY = scaleY * zoomY;
    
    const dataScreenW = width3D * baseScaleX;
    const dataScreenH = heightScale3D * baseScaleY;
    const limitX = Math.max(0, dataScreenW / 2 + boxW * 0.3);
    const limitY = Math.max(0, dataScreenH / 2 + boxH * 0.3);

    if (transformRef.current.panX > limitX) transformRef.current.panX = limitX;
    if (transformRef.current.panX < -limitX) transformRef.current.panX = -limitX;
    if (transformRef.current.panY > limitY) transformRef.current.panY = limitY;
    if (transformRef.current.panY < -limitY) transformRef.current.panY = -limitY;

    const centerX = boxLeft + boxW / 2 + transformRef.current.panX;
    const centerY = boxTop + boxH / 2 + transformRef.current.panY;
    
    const toScreenX = (dataX: number) => centerX + dataX * baseScaleX;
    const toScreenY = (dataY: number) => centerY - dataY * baseScaleY;
    const toDataX = (screenX: number) => (screenX - centerX) / baseScaleX;
    const toDataY = (screenY: number) => (centerY - screenY) / baseScaleY;

    const validCandles = data.filter(d => (d as any).isRealCandle !== false && d.open !== undefined);
    
    let timeMin = 0;
    let timeMax = 1;
    let timeDiff = 1;
    if (validCandles.length > 0) {
      timeMin = validCandles[0].timestamp || 0;
      timeMax = validCandles[validCandles.length - 1].timestamp || 0;
      timeDiff = Math.max(timeMax - timeMin, 1);
    } else {
      timeMin = data[0].timestamp || 0;
      timeMax = data[data.length - 1].timestamp || 0;
      timeDiff = Math.max(timeMax - timeMin, 1);
    }
    
    const timeToDataX = (t: number) => startX + ((t - timeMin) / timeDiff) * width3D;
    const dataXToTime = (dataX: number) => timeMin + ((dataX - startX) / width3D) * timeDiff;
    
    const visDataXMin = toDataX(boxLeft);
    const visDataXMax = toDataX(boxRight);
    const visTimeMin = dataXToTime(visDataXMin);
    const visTimeMax = dataXToTime(visDataXMax);
    
    const getPriceLocal = (dataY: number) => {
        const diff = maxP - minP || 1;
        return ((dataY / heightScale3D) + 0.5) * diff + minP;
    };

    const visYBottom = toDataY(boxBottom);
    const visYTop = toDataY(boxTop);
    const visPMin = getPriceLocal(visYBottom);
    const visPMax = getPriceLocal(visYTop);
    
    ctx.fillStyle = '#000000';
    ctx.fillRect(boxLeft, boxTop, boxW, boxH);

    ctx.save();
    ctx.beginPath();
    ctx.rect(boxLeft, boxTop, boxW, boxH);
    ctx.clip();
    
    // Draw Period Separators
    if (showPeriodSeparators && data.length > 1) {
      ctx.save();
      const rgbaPeriod = hexToRgba(periodColor || '#ffffff', periodOpacity || 0.3);
      ctx.strokeStyle = rgbaPeriod;
      ctx.lineWidth = (periodLineWidth || 1) * 2;
      if (periodLineStyle === 'dashed') ctx.setLineDash([4, 4]);
      else if ((periodLineStyle as any) === 'dotted') ctx.setLineDash([2, 4]);
      
      ctx.beginPath();
      const baseSepType = getTimeframeSeparation(timeframe);
      const dynamicSepType = getDynamicPeriodSeparation(baseSepType, timeDiff / zoomX, boxW, 40);
      const sepIndices = getSeparatorIndices(data, timeframe, dynamicSepType);
      
      for (const sep of sepIndices) {
        if ((data[sep.index] as any).isRealCandle === false) continue;
        
        const px = toScreenX(startX + data[sep.index].time * width3D);
        if (px >= boxLeft && px <= boxRight) {
          ctx.moveTo(px, boxTop);
          ctx.lineTo(px, boxBottom);
          
          ctx.fillStyle = rgbaPeriod;
          ctx.font = '600 11px "Inter", ui-sans-serif, system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(sep.label, px, boxTop + 12);
        }
      }
      
      ctx.stroke();
      ctx.restore();
    }

    ctx.lineWidth = gridThickness;
    ctx.strokeStyle = rgbaGrid;

    // Vertical Time Grid Lines
    const timeStep = getTimeTickInterval(visTimeMin, visTimeMax, Math.max(5, Math.floor(boxW / 80)));
    const firstTime = Math.floor(visTimeMin / timeStep) * timeStep;
    for (let t = firstTime; t <= visTimeMax + timeStep * 0.5; t += timeStep) {
      const px = toScreenX(timeToDataX(t));
      if (px >= boxLeft - 0.5 && px <= boxRight + 0.5) {
        ctx.beginPath();
        ctx.moveTo(px, boxTop);
        ctx.lineTo(px, boxBottom);
        ctx.stroke();
      }
    }
    
    // Horizontal Price Grid Lines
    const realVisPMin = Math.min(visPMin, visPMax);
    const realVisPMax = Math.max(visPMin, visPMax);
    const priceStep = getTickInterval(realVisPMin, realVisPMax, Math.max(5, Math.floor(boxH / 50)));
    const firstPrice = Math.floor(realVisPMin / priceStep) * priceStep;
    for (let p = firstPrice; p <= realVisPMax + priceStep * 0.5; p += priceStep) {
      const py = toScreenY(getY(p));
      if (py >= boxTop - 0.5 && py <= boxBottom + 0.5) {
        ctx.beginPath();
        ctx.moveTo(boxLeft, py);
        ctx.lineTo(boxRight, py);
        ctx.stroke();
      }
    }
    
    const candleCount = validCandles.length;
    const baseCandleWidth = candleCount > 0 ? (width3D / candleCount) * 0.6 : 0;
    const cw = baseCandleWidth * baseScaleX;
    const wickW = Math.max(cw * 0.15, 1);
    
    let lastRealPx = 0;
    let lastRealPy = 0;
    let lastCandlePrice = 0;

    validCandles.forEach(d => {
        if (d.open === undefined || d.close === undefined || d.high === undefined || d.low === undefined) return;
        const x = toScreenX(startX + d.time * width3D);
        if (x + cw/2 < boxLeft || x - cw/2 > boxRight) return;
        
        const yOpen = toScreenY(getY(d.open));
        const yClose = toScreenY(getY(d.close));
        const yHigh = toScreenY(getY(d.high));
        const yLow = toScreenY(getY(d.low));
        
        const isGreen = d.close >= d.open;
        ctx.fillStyle = isGreen ? colorUp : colorDown;
        ctx.fillRect(x - wickW / 2, Math.min(yHigh, yLow), wickW, Math.abs(yHigh - yLow));
        const bodyTop = Math.min(yOpen, yClose);
        const bodyHeight = Math.max(Math.abs(yOpen - yClose), 1);
        ctx.fillRect(x - cw / 2, bodyTop, cw, bodyHeight);
    });

    // Diagnostic 2 Fix: Always use the exact last element of validCandles for the live price track,
    // avoiding the issue where it gets skipped/misaligned by viewport clipping
    const actualLastCandle = validCandles[validCandles.length - 1];
    if (actualLastCandle && actualLastCandle.close !== undefined) {
      lastRealPx = toScreenX(startX + actualLastCandle.time * width3D);
      lastRealPy = toScreenY(getY(actualLastCandle.close));
      lastCandlePrice = actualLastCandle.close;
    }

    const drawSMA = (key: 'sma10'|'sma20'|'sma50', color: string) => {
        ctx.beginPath();
        let started = false;
        data.forEach(d => {
            if (d[key] !== undefined && (d as any).isRealCandle !== false) {
                const x = toScreenX(startX + d.time * width3D);
                const y = toScreenY(getY(d[key] as number));
                if (x >= boxLeft - 50 && x <= boxRight + 50) {
                  if (!started) {
                      ctx.moveTo(x, y);
                      started = true;
                  } else {
                      ctx.lineTo(x, y);
                  }
                } else if (started && x > boxRight + 50) {
                  ctx.lineTo(x, y);
                  started = false;
                }
            }
        });
        if (started) {
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.strokeStyle = color;
            ctx.lineWidth = 1.5;
            ctx.stroke();
        }
    };
    
    if (showSMA10) drawSMA('sma10', '#a855f7');
    if (showSMA20) drawSMA('sma20', '#38bdf8');
    if (showSMA50) drawSMA('sma50', '#a855f7'); 

    if (validCandles.length > 0) {
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(lastRealPx, lastRealPy);
        ctx.lineTo(boxRight, lastRealPy);
        const isUp = lastCandlePrice >= (validCandles[validCandles.length - 2]?.close || lastCandlePrice);
        const priceColor = isUp ? colorUp : colorDown;
        ctx.strokeStyle = priceColor;
        ctx.lineWidth = 1;
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(lastRealPx, boxBottom);
        ctx.lineTo(lastRealPx, lastRealPy);
        ctx.stroke();
        
        ctx.setLineDash([]);
    }

    ctx.restore(); 
    
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(boxLeft, boxTop, boxW, boxH);

    const drawnRects: {l: number, r: number, t: number, b: number}[] = [];
    const checkCollision = (text: string, tx: number, ty: number): boolean => {
      const metrics = ctx.measureText(text);
      let l = tx, r = tx, t = ty, b = ty;
      const align = ctx.textAlign;
      const baseline = ctx.textBaseline;
      if (align === 'center') {
        l = tx - metrics.width / 2; r = tx + metrics.width / 2;
      } else if (align === 'right' || align === 'end') {
        l = tx - metrics.width; r = tx;
      } else {
        l = tx; r = tx + metrics.width;
      }
      if (baseline === 'middle') {
        t = ty - 6; b = ty + 6;
      } else if (baseline === 'bottom' || baseline === 'alphabetic') {
        t = ty - 12; b = ty;
      } else if (baseline === 'top') {
        t = ty; b = ty + 12;
      }
      const pad = 6;
      for (const rect of drawnRects) {
        if (l < rect.r + pad && r > rect.l - pad && t < rect.b + pad && b > rect.t - pad) return true;
      }
      drawnRects.push({l, r, t, b});
      return false;
    };
    
    const drawTextOutline = (text: string, tx: number, ty: number, col = 'rgba(255, 255, 255, 0.85)') => {
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#000000';
      ctx.strokeText(text, tx, ty);
      ctx.fillStyle = col;
      ctx.fillText(text, tx, ty);
    };

    let liveCandleInfo: {
      priceColor: string;
      priceTxt: string;
      timeTxt: string;
      priceBoxWidth: number;
      timeBoxWidth: number;
    } | null = null;

    if (validCandles.length > 0) {
      const isUp = lastCandlePrice >= (validCandles[validCandles.length - 2]?.close || lastCandlePrice);
      const priceColor = isUp ? colorUp : colorDown;
      const padX = 6;

      const pDecimals = lastCandlePrice >= 1000 ? 1 : 2;
      const priceTxt = formatAbbrev(lastCandlePrice, pDecimals);
      ctx.font = "bold 11px Inter, ui-sans-serif, system-ui, sans-serif";
      const pTxtWidth = ctx.measureText(priceTxt).width;
      const priceBoxWidth = pTxtWidth + padX * 2;

      const lastCandleTs = validCandles[validCandles.length - 1].timestamp || Date.now();
      const d = new Date(lastCandleTs);
      const timeTxt = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
      const tTxtWidth = ctx.measureText(timeTxt).width;
      const timeBoxWidth = tTxtWidth + padX * 2;

      liveCandleInfo = {
        priceColor,
        priceTxt,
        timeTxt,
        priceBoxWidth,
        timeBoxWidth
      };

      // Suppress any axis ticks under live badges
      drawnRects.push({
        l: boxRight - priceBoxWidth - 6,
        r: boxRight,
        t: lastRealPy - 12,
        b: lastRealPy + 12
      });
      drawnRects.push({
        l: lastRealPx - timeBoxWidth / 2 - 6,
        r: lastRealPx + timeBoxWidth / 2 + 6,
        t: boxBottom - 24,
        b: boxBottom
      });
    }

    ctx.font = '600 11px "Inter", ui-sans-serif, system-ui, sans-serif';
    
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const visTimeRange = Math.max(1000, visTimeMax - visTimeMin);
    for (let t = firstTime; t <= visTimeMax + timeStep * 0.5; t += timeStep) {
      const px = toScreenX(timeToDataX(t));
      if (px >= boxLeft && px <= boxRight) {
        const txt = formatTime(t, visTimeRange);
        if (checkCollision(txt, px, boxBottom - 8)) continue;
        ctx.beginPath();
        ctx.moveTo(px, boxBottom);
        ctx.lineTo(px, boxBottom - 5);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 1;
        ctx.stroke();
        drawTextOutline(txt, px, boxBottom - 8, 'rgba(255, 255, 255, 0.8)');
      }
    }
    
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let p = firstPrice; p <= realVisPMax + priceStep * 0.5; p += priceStep) {
      const py = toScreenY(getY(p));
      if (py >= boxTop && py <= boxBottom) {
        const txt = formatAbbrev(p, p > 1000 ? 1 : 2);
        if (checkCollision(txt, boxRight - 8, py)) continue;
        ctx.beginPath();
        ctx.moveTo(boxRight, py);
        ctx.lineTo(boxRight - 5, py);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 1;
        ctx.stroke();
        drawTextOutline(txt, boxRight - 8, py, 'rgba(255, 255, 255, 0.8)');
      }
    }
    
    // Draw current price and time labels AFTER axes
    if (liveCandleInfo) {
        const { priceColor, priceTxt, timeTxt, priceBoxWidth, timeBoxWidth } = liveCandleInfo;
        
        ctx.font = "bold 11px Inter, ui-sans-serif, system-ui, sans-serif";
        ctx.fillStyle = priceColor;
        ctx.fillRect(boxRight - priceBoxWidth, lastRealPy - 10, priceBoxWidth, 20);
        ctx.fillStyle = "#000000";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(priceTxt, boxRight - priceBoxWidth / 2, lastRealPy);
        
        ctx.fillStyle = priceColor;
        ctx.fillRect(lastRealPx - timeBoxWidth / 2, boxBottom - 20, timeBoxWidth, 20);
        ctx.fillStyle = "#000000";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(timeTxt, lastRealPx, boxBottom - 10);
    }
    
  }, [
    data, layout.startX, width3D, heightScale3D, getY, minP, maxP,
    gridColor, gridOpacity, gridThickness, colorUp, colorDown,
    showSMA10, showSMA20, showSMA50
  ]);

  renderCanvasRef.current = renderCanvas;

  useEffect(() => {
    if (isOpen) {
      scheduleRender();
    }
  }, [isOpen, scheduleRender]);


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black font-mono text-white">
      {/* Header Bar */}
      <div className="absolute top-0 inset-x-0 z-20 flex justify-between items-start p-3 pt-[calc(0.75rem+env(safe-area-inset-top,0px))] bg-gradient-to-b from-black/70 to-transparent pointer-events-none [&>*]:pointer-events-auto">
        <CoreHUD 
          isEditingSymbol={isEditingSymbol!}
          setIsEditingSymbol={setIsEditingSymbol!}
          symbol={symbol!}
          setSymbol={setSymbol!}
          timeframe={timeframe!}
          lastPrice={lastPrice!}
          priceChange={priceChange!}
          currentMomentum={data && data.length > 0 ? data[data.length - 1].momentum : null}
          candleCloseTime={data && data.length > 0 ? data[data.length - 1].closeTime : undefined}
          latencyMs={latencyMs!}
        />
        <div className="flex items-center gap-2 shrink-0">
          <button 
            onClick={resetTransform}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/5 hover:bg-white/15 border border-white/10 rounded-md transition-colors text-zinc-300 hover:text-white"
            title="Restaurar zoom e posição padrão"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Centralizar</span>
          </button>
          <button 
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded-md transition-colors text-zinc-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Chart Container */}
      <div className="absolute top-0 left-[env(safe-area-inset-left,0px)] right-[env(safe-area-inset-right,0px)] bottom-[env(safe-area-inset-bottom,0px)] flex flex-col">
        <div 
          className="flex-1 w-full h-full relative"
          ref={containerRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onWheel={handleWheel}
          style={{ touchAction: 'none' }}
        >
          <canvas 
            ref={canvasRef}
            className="absolute inset-0 block w-full h-full cursor-crosshair"
          />
          <div 
            className="absolute inset-0 pointer-events-none transition-opacity duration-1000 ease-in-out z-10" 
            style={{ opacity: labelOpacity }}
          >
            {/* Y Axis Label */}
            <div className="absolute right-16 top-1/2 -translate-y-1/2 -rotate-90 flex items-center justify-center origin-center h-0 w-0">
              <div className="text-[11px] font-semibold text-white/35 tracking-wider whitespace-nowrap select-none">
                PREÇO
              </div>
            </div>
            {/* X Axis Label */}
            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-[11px] font-semibold text-white/35 tracking-wider whitespace-nowrap select-none">
              TEMPO
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
